import { t } from '../../i18next';

export type MakeOrBuyInputType = {
  bestellNummer?: string;
  debitorName?: string;
  designOwnerId?: number;
  htzTypeId?: number;
  khkPkey?: number;
  lumpSumDue?: Date;
  makeOrBuy: number;
  makeOrBuyModified?: boolean;
  noveltyId?: number;
  npiAcceptPO?: number;
  npiAsKenner?: string;
  npiComment?: string;
  nrpDue?: Date;
  pagDeliveryDateDue?: Date;
  pagFollowUpDate?: Date;
  pagIsPartOf?: string;
  poKubu?: string;
  preHtz?: string;
  preHtzManufacturedBy?: string;
  projectId?: number;
  rpDue?: Date;
  sheet1Htz?: string;
};

export type RulesResultType = {
  data?: { table: string; fields: Map<string, boolean | number | string> }[];
  error?: string;
  save: boolean;
};

export function makeOrBuyRules(inputData: MakeOrBuyInputType): RulesResultType {
  const { makeOrBuy } = inputData;

  if ([1, 16, 17, 18, 19].includes(makeOrBuy)) {
    return { save: true };
  }

  if ([3, 4, 5, 6, 7, 8, 9, 10, 11, 15, 20, 21].includes(makeOrBuy)) {
    return makeOrBuyDecided(inputData);
  }

  if (makeOrBuy === 2) {
    return reIndustrialize(inputData);
  }

  if (makeOrBuy === 14) {
    return industrializationOnHold(inputData);
  }

  if ([12, 13].includes(makeOrBuy)) {
    return makeOrBuyRejected(inputData);
  }
}

function makeOrBuyRejected(inputData: MakeOrBuyInputType): RulesResultType {
  const {
    bestellNummer,
    makeOrBuy,
    makeOrBuyModified,
    npiAcceptPO,
    npiComment,
    poKubu,
    preHtz,
    preHtzManufacturedBy,
    sheet1Htz,
  } = inputData;

  const result = { save: true, data: null };

  if (npiAcceptPO) {
    return { save: false, error: t('mob.NoRejectWhenPOAccepted') };
  } else if (!npiComment || npiComment.trim().length === 0) {
    return { save: false, error: t('mob.CommentRejectReason') };
  }

  if (bestellNummer !== null) {
    result.data = [setEsaph({ poBlocked: 1, poAction: 1 })];

    if (makeOrBuyAndHtzCheck(makeOrBuyModified, sheet1Htz)) {
      return result;
    }

    switch (poKubu) {
      case 'Accepted':
        result.data.push(
          setIndustrialization({
            industrializationId: IndustrializationId.Industrialize,
            makeOrBuy,
            preHtz,
            preHtzManufacturedBy,
          }),
        );
        return result;
      case 'Cancelled':
        result.data.push(
          setIndustrialization({
            industrializationId: getIndusIdForRejection(makeOrBuy),
            makeOrBuy,
            preHtz,
            preHtzManufacturedBy,
          }),
        );
        return result;
      default:
        result.data.push(
          setIndustrialization({
            industrializationId: IndustrializationId.WaitForPOCancellation,
            makeOrBuy,
            preHtz,
            preHtzManufacturedBy,
          }),
        );
        return result;
    }
  } else {
    if (makeOrBuyAndHtzCheck(makeOrBuyModified, sheet1Htz)) {
      return result;
    }

    const industrializationId: IndustrializationId = getIndusIdForRejection(makeOrBuy);
    result.data = [
      setIndustrialization({ industrializationId, makeOrBuy, preHtz, preHtzManufacturedBy }),
    ];
  }

  return result;
}

function reIndustrialize(inputData: MakeOrBuyInputType): RulesResultType {
  const {
    bestellNummer,
    lumpSumDue,
    makeOrBuy,
    makeOrBuyModified,
    npiAcceptPO,
    nrpDue,
    pagDeliveryDateDue,
    preHtz,
    preHtzManufacturedBy,
    rpDue,
    sheet1Htz,
    poKubu,
  } = inputData;

  if (bestellNummer !== null) {
    const result: RulesResultType = { save: true, data: [] };
    const atLeastOneDueDateSet = lumpSumDue || nrpDue || pagDeliveryDateDue || rpDue;

    if (npiAcceptPO === 1 && atLeastOneDueDateSet) {
      return { save: false, error: t('mob.CannotAcceptPOAndAskForDue') };
    }
    if (!atLeastOneDueDateSet) {
      result.data.push(setEsaph({ poBlocked: 0, poAction: 2 }));
    }
    if (npiAcceptPO === 0 && atLeastOneDueDateSet) {
      result.data.push(setEsaph({ poBlocked: 1, poAction: 3 }));
    }

    if (makeOrBuyAndHtzCheck(makeOrBuyModified, sheet1Htz)) {
      return result;
    }

    switch (poKubu) {
      case 'Accepted':
        result.data.push(
          setIndustrialization({
            industrializationId: IndustrializationId.Industrialize,
            makeOrBuy,
            preHtz,
            preHtzManufacturedBy,
          }),
        );
        return result;
      case 'Cancelled':
        result.data.push(
          setIndustrialization({
            industrializationId: IndustrializationId.NoIndustrialization,
            makeOrBuy,
            preHtz,
            preHtzManufacturedBy,
          }),
        );
        return result;
      case 'In Cancellation':
        result.data.push(
          setIndustrialization({
            industrializationId: IndustrializationId.WaitForPOCancellation,
            makeOrBuy,
            preHtz,
            preHtzManufacturedBy,
          }),
        );
        return result;
      default:
        result.data.push(
          setIndustrialization({
            industrializationId: IndustrializationId.WaitForPOAgreement,
            makeOrBuy,
            preHtz,
            preHtzManufacturedBy,
          }),
        );
        return result;
    }
  } else {
    return {
      save: true,
      data: [
        setIndustrialization({
          industrializationId: IndustrializationId.Industrialize,
          makeOrBuy,
          preHtz,
          preHtzManufacturedBy,
        }),
      ],
    };
  }
}

function makeOrBuyDecided(inputData: MakeOrBuyInputType): RulesResultType {
  const {
    bestellNummer,
    debitorName,
    designOwnerId,
    htzTypeId,
    khkPkey,
    makeOrBuy,
    makeOrBuyModified,
    noveltyId,
    npiAsKenner,
    pagIsPartOf,
    preHtz,
    preHtzManufacturedBy,
    projectId,
    sheet1Htz,
  } = inputData;

  if (!designOwnerId || !htzTypeId || !khkPkey || !noveltyId || !npiAsKenner) {
    return { save: false, error: t('mob.MakeOrBuyFirstMandatory') };
  }

  if (
    (!debitorName || !bestellNummer) &&
    designOwnerId === 2 &&
    projectId !== 10 &&
    htzTypeId === 4
  ) {
    return { save: false, error: t('mob.MakeOrBuySecondMandatory') };
  }

  if (!pagIsPartOf && designOwnerId === 2 && projectId !== 10 && htzTypeId === 5) {
    return { save: false, error: t('mob.MakeOrBuyThirdMandatory') };
  }

  if (bestellNummer) {
    return reIndustrialize(inputData);
  }

  if (makeOrBuyAndHtzCheck(makeOrBuyModified, sheet1Htz)) {
    return { save: true };
  }

  if (makeOrBuyModified === true && sheet1Htz !== null) {
    return {
      save: true,
      data: [
        setIndustrialization({
          industrializationId: IndustrializationId.Industrialize,
          makeOrBuy,
          preHtz,
          preHtzManufacturedBy,
        }),
      ],
    };
  } else {
    return { save: true };
  }
}

function industrializationOnHold(inputData: MakeOrBuyInputType): RulesResultType {
  const { preHtz, makeOrBuy, makeOrBuyModified, pagFollowUpDate, preHtzManufacturedBy, sheet1Htz } =
    inputData;

  if (!pagFollowUpDate) {
    return { save: false, error: t('mob.SetFollowUpDate') };
  }

  if (makeOrBuyAndHtzCheck(makeOrBuyModified, sheet1Htz)) {
    return { save: true };
  }

  if (makeOrBuyModified === true && sheet1Htz !== null) {
    return {
      save: true,
      data: [
        setIndustrialization({
          industrializationId: IndustrializationId.IndustrializationOnHold,
          makeOrBuy,
          preHtz,
          preHtzManufacturedBy,
        }),
      ],
    };
  }
}

export function makeOrBuyAndHtzCheck(makeOrBuyModified, sheet1Htz) {
  return makeOrBuyModified === false || (makeOrBuyModified === true && !sheet1Htz);
}

export function getIndusIdForRejection(makeOrBuy: number) {
  return makeOrBuy === 12
    ? IndustrializationId.NoIndustrialization
    : IndustrializationId.NoIndustrializationEffortCollect;
}

type IndustrializationParamType = {
  industrializationId: IndustrializationId;
  makeOrBuy: number;
  preHtz: string;
  preHtzManufacturedBy: string;
};

export function setIndustrialization(indusData: IndustrializationParamType) {
  const { industrializationId, makeOrBuy, preHtz, preHtzManufacturedBy } = indusData;

  const makeOrBuyToWerk = new Map([
    [3, '1100'],
    [4, '1400'],
    [5, '1200'],
    [6, '1300'],
    [7, '1500'],
    [9, '1400'],
    [10, '1300'],
    [11, '1100'],
    [15, '1200'],
  ]);

  const sheet1Update = {
    table: 'sheet1',
    fields: new Map<string, number | string>([
      ['indus_id', industrializationId],
      ['indus_id_lastmodified', new Date().toISOString().substring(0, 10)],
      ['pre_htz', preHtz],
      ['pre_htz_manufactured_by', preHtzManufacturedBy],
    ]),
  };

  if (makeOrBuyToWerk.get(makeOrBuy)) {
    sheet1Update.fields.set('werk', makeOrBuyToWerk.get(makeOrBuy));
  }

  return sheet1Update;
}

export function setEsaph({ poBlocked, poAction }) {
  return {
    table: 'e_saph',
    fields: new Map<string, boolean | number | string>([
      ['po_blocked', poBlocked],
      ['po_action_id', poAction],
    ]),
  };
}

export const IndustrializationId = {
  WaitForPOCancellation: 2,
  WaitForPOAgreement: 3,
  Industrialize: 4,
  NoIndustrialization: 5,
  IndustrializationOnHold: 6,
  NoIndustrializationEffortCollect: 7,
} as const;

export type IndustrializationId = (typeof IndustrializationId)[keyof typeof IndustrializationId];
